import { Component, OnInit } from "@angular/core";
import { DomSanitizer, Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";

import { getFileExtension } from "../../shared/helper-functions";
import { Mimetype } from "../../shared/mimetype";
import { MomentPipe } from "../../shared/moment.pipe";
import { Path } from "../../shared/path";
import { DocumentData } from "../annotation-controller/document-data.type";
import { OneDriveItem } from "../onedrive-list/onedrive-item.type";
import { CannotPreviewViewerContent, FileViewerConfig } from "../viewer-controller/file-viewer/file-viewer.type";
import { InfoPanelConfig } from "../viewer-controller/viewer-info-panel/viewer-info-panel.types";
import { ViewerTopBarConfig } from "../viewer-controller/viewer-top-bar/viewer-top-bar.component";

@Component({
   selector: "app-onedrive-viewer",
   templateUrl: "./onedrive-viewer.component.html",
   styleUrls: ["./onedrive-viewer.component.css"],
})
export class OnedriveViewerComponent implements OnInit {
   driveItem: OneDriveItem;

   topBarConfig: ViewerTopBarConfig;
   viewerConfig: FileViewerConfig;
   infoPanelConfig: InfoPanelConfig;

   constructor(
      private titleService: Title,
      private route: ActivatedRoute,
      private router: Router,
      private sanitizer: DomSanitizer,
   ) {}

   ngOnInit(): void {
      this.route.paramMap.subscribe((params) => {
         this.driveItem = this.decodeAndParseJson<OneDriveItem>(params.get("driveitem"));

         if (!this.driveItem) return;
         if (!this.driveItem.file) return;

         this.titleService.setTitle("zDOX - OneDrive - " + this.driveItem.name);

         this.topBarConfig = this.setTopBarConfig(this.driveItem);
         this.infoPanelConfig = this.setInfoPanelConfig(this.driveItem);
         this.setViewerConfig(this.driveItem);
      });
   }

   private decodeAndParseJson<T>(encodedJson: string): T | null {
      let dataObject: T;
      try {
         const decodedJson = decodeURIComponent(encodedJson);
         dataObject = JSON.parse(decodedJson);
      } catch (e) {
         console.error("Error decoding and parsing JSON:", e);
      } finally {
         return dataObject;
      }
   }

   //=============================================================================
   // MAPPING DRIVEITEM TO VIEWER CONFIG
   //=============================================================================

   private setTopBarConfig(driveItem: OneDriveItem): ViewerTopBarConfig {
      const topBarConfig: ViewerTopBarConfig = {
         viewMode: "default",
         fileName: driveItem.name,
         lastModifiedTime: new MomentPipe().transform(driveItem.lastModifiedDateTime),
         showBackButton: true,
         showLogo: false,
         showShareButton: false,
         showOpenWithButton: false,
         showDownloadButton: false,
         showToggleInfoPanelButton: false,
      };
      return topBarConfig;
   }

   private setInfoPanelConfig(driveItem: OneDriveItem): InfoPanelConfig {
      const infoPanelConfig: InfoPanelConfig = {
         enableInfoPanel: false,
      };
      return infoPanelConfig;
   }

   private async setViewerConfig(driveItem: OneDriveItem) {
      // PDFTron Viewer
      if (
         [
            "application/pdf",
            "application/vnd.oasis.opendocument.text",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            "application/msword",
            "application/vnd.ms-excel",
            "application/vnd.ms-powerpoint",
            "image/jpeg",
            "image/png",
            "image/tiff",
         ].includes(driveItem.file.mimeType) &&
         getFileExtension(driveItem.name) !== "csv"
      ) {
         this.viewerConfig = {
            viewerType: "document",
            content: {
               actionType: "dms",
               allowDownloadAndPrint: true,
               fileName: driveItem.name,
               isPermissions: {
                  print: true,
                  edit: false,
                  create: false,
                  delete: false,
                  write: false,
               },
               mimeType: driveItem.file.mimeType,
               url: driveItem["@microsoft.graph.downloadUrl"],
            } as DocumentData,
         };
      }
      // Image (not supported by PDFTron)
      else if (["image/gif", "image/bmp"].includes(driveItem.file.mimeType)) {
         this.topBarConfig.showDownloadButton = true;

         this.viewerConfig = {
            viewerType: "image",
            content: driveItem["@microsoft.graph.downloadUrl"],
         };
      }
      // SVG
      else if (driveItem.file.mimeType === "image/svg+xml" || getFileExtension(driveItem.name) === "svg") {
         this.topBarConfig.showDownloadButton = true;

         const unsafe_content = await fetch(driveItem["@microsoft.graph.downloadUrl"]).then((res) => res.text());
         const safe_content = this.sanitizer.bypassSecurityTrustHtml(unsafe_content); // Not really safe 🥲
         this.viewerConfig = {
            viewerType: "svg",
            content: safe_content,
         };
      }
      // Video
      else if (
         driveItem.file.mimeType.indexOf("video/") === 0 &&
         driveItem.file.mimeType !== "video/mpeg" &&
         driveItem.file.mimeType !== "video/avi" &&
         driveItem.file.mimeType !== "video/msvideo" &&
         driveItem.file.mimeType !== "video/x-msvideo"
      ) {
         this.topBarConfig.showDownloadButton = true;
         this.viewerConfig = {
            viewerType: "video",
            content: driveItem["@microsoft.graph.downloadUrl"],
         };
      }
      // Audio
      else if (driveItem.file.mimeType.indexOf("audio/") === 0) {
         this.topBarConfig.showDownloadButton = true;
         this.viewerConfig = {
            viewerType: "audio",
            content: driveItem["@microsoft.graph.downloadUrl"],
         };
      }
      // Plain Text (.txt, .xml)
      else if (driveItem.file.mimeType.indexOf("text/") === 0 || driveItem.file.mimeType === "application/xml") {
         this.topBarConfig.showDownloadButton = true;
         const content = await fetch(driveItem["@microsoft.graph.downloadUrl"]).then((res) => res.text());
         this.viewerConfig = {
            viewerType: "text",
            content: content,
         };
      }
      // Other/Cannot Preview
      else {
         this.viewerConfig = {
            viewerType: "other",
            content: {
               fileName: driveItem?.name,
               fileSizeByte: driveItem?.size,
               allowDownload: Boolean(driveItem),
               iconPath: Mimetype.getIconOneDrive(driveItem),
            } as CannotPreviewViewerContent,
         };
      }
   }

   //=============================================================================
   // HANDLING EMITTED EVENT FROM APP-VIEWER-CONTROLLER
   //=============================================================================

   back() {
      const path = () => {
         if (!this.driveItem) {
            return "/files";
         } else if (this.driveItem.parentReference) {
            return Path.combine("/onedrivepage", this.driveItem.parentReference.id);
         } else {
            return "/onedrivepage";
         }
      };
      this.router.navigate([path()]);
   }

   download() {
      location.href = this.driveItem["@microsoft.graph.downloadUrl"];
   }

   openWith() {
      console.error("Method not implemented");
   }
}
